import type { SimState } from '../types'
import { formatSimState } from '../lib/metricFormat'

/**
 * Compact SIM-card glyph for the phones table. Colour + overlay encode the
 * SIM state; the full Russian label rides along as `aria-label` / native
 * tooltip. `network_locked` shares the lock glyph with `locked` — the two
 * differ only in the tooltip text.
 */

const STATE_COLOR: Record<SimState, string> = {
  ready: 'text-emerald-500',
  absent: 'text-gray-400 dark:text-gray-500',
  locked: 'text-amber-500',
  network_locked: 'text-amber-500',
  unknown: 'text-gray-400 dark:text-gray-500',
}

export function SimStatusIcon({ state }: { state: SimState | null }) {
  if (state === null) {
    return <span className="text-gray-400 dark:text-gray-600">—</span>
  }
  const label = formatSimState(state)
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox="0 0 20 20"
      className={`h-5 w-5 ${STATE_COLOR[state]}`}
    >
      <title>{label}</title>
      {/* SIM-card outline — cut top-right corner */}
      <path
        d="M4 2.5 H13 L16 5.5 V17.5 H4 Z"
        fill="currentColor"
        fillOpacity={0.18}
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {state === 'absent' && (
        <line
          x1="3.5"
          y1="16.5"
          x2="16.5"
          y2="3.5"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
        />
      )}
      {(state === 'locked' || state === 'network_locked') && (
        <>
          <path
            d="M8.7 10.6 V9 a1.3 1.3 0 0 1 2.6 0 V10.6"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            strokeLinecap="round"
          />
          <rect x="7.4" y="10.4" width="5.2" height="4.6" rx="0.8" fill="currentColor" />
        </>
      )}
      {state === 'unknown' && (
        <text
          x="10"
          y="13.6"
          textAnchor="middle"
          fontSize="9"
          fontWeight="700"
          fill="currentColor"
        >
          ?
        </text>
      )}
    </svg>
  )
}
